/* eslint-disable camelcase */
import posthog from "posthog-js"
import { cookie, eraseCookie } from "@/utils/cookie"
import { shallowEqualObjects } from "shallow-equal"
import { onPageNavigation } from "@/utils/location"
// TODO: get from posthog-js
const STORED_PERSON_PROPERTIES_KEY = "$stored_person_properties"

// do_logout sets this so the next page load unlinks the device from the user
// who logged out. Reading a cookie (vs a URL param) survives every logout
// redirect target, and resetting before identify() runs stops the next user
// from being merged into the previous one.
const RESET_ON_LOGOUT_COOKIE = "ph_reset"

const isTestUser = () => cookie("testautomation") === "allow"

export default function init(config = {}) {
  posthog.init("phc_10RZrT449APITxnM0KDP3E4iUf4pl9rxTAQ96G39Mgs", {
    // use api host without the term posthog to prevent ad blockers from blocking the request
    // https://posthog.com/docs/advanced/proxy/cloudflare#method-two-proxy-using-dns-and-page-rules-with-cloudflare-enterprise
    api_host: "https://pineapple.blinkist.com",
    ui_host: "https://eu.posthog.com",
    /**
     * https://posthog.com/docs/data/persons
     * "identified_only" will create a new person once `posthog.identify` is called i.e., when a user logs in
     * We do this to reduce billing costs as anonymous users are x4 cheaper than identified users
     */
    person_profiles: "identified_only",
    persistence: "localStorage",
    capture_pageview: false, // manually capture pageview
    enable_recording_console_log: true,
    enable_heatmaps: true,
    autocapture: false,
    // disable feature flags by default to reduce usage
    // specifically set to false in areas where we want to use feature flags
    advanced_disable_feature_flags: false,
    // Session recording is gated by PostHog's native recording-conditions
    // config (URL triggers + linked feature flag + global sample rate),
    // not by client code. See WEB-11.
    ...config
  })

  // Runs before identify() (gated behind the async mickeyMouse call), so the
  // logged-out user's distinct_id is gone before the next user is identified.
  if (cookie(RESET_ON_LOGOUT_COOKIE)) {
    posthog.reset()
    eraseCookie(RESET_ON_LOGOUT_COOKIE)
  }

  onPageNavigation(() => {
    posthog.capture("$pageview", {
      $current_url: window.location.href,
      test: isTestUser()
    })
  })

  window.addEventListener("CookiebotOnConsentReady", () => {
    posthog.set_config({
      persistence: window.Cookiebot.consent.statistics ? "localStorage+cookie" : "localStorage"
    })
  })

  window.addEventListener("CookiebotOnDialogDisplay", () => {
    const isUnintrusive = () => {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get("consent") === "ni"
    }

    posthog.capture("cookie_consent_view", {
      consentType: isUnintrusive() ? "unintrusive" : "intrusive"
    })
  })

}

/**
 *
 * only identify and set user properties once user is signed up
 * this reduces cost as anonymous users are x4 cheaper
 * https://posthog.com/docs/product-analytics/identify
 */
export const posthogIdentifyRegisteredUser = async (user) => {
  // const { enabled } = await getFeature("identify_user_posthog_bw_enabled")
  // if (!enabled) return

  if (user.user_id === null || user.user_id === undefined) return user

  posthog.identify(user.user_id)

  // update properties only if one has changed to reduce event cost
  const updatedProperties = getUpdatedProperties(user)

  if (updatedProperties !== null) {
    posthog.capture("$set", { $set: updatedProperties })
  }

  return user
}

/**
 * Return user properties if one has changed compared to posthog
 * If the user is the same, return null
 */
const getUpdatedProperties = (user) => {
  const newUserProps = {
    has_mobile_app: user.has_mobile_app,
    has_trialed: user.has_trialed,
    will_renew: user.will_renew,
    guest: user.guest,
    segment: user.segment,
    b2b_role: user.b2b_role,
    b2b_organisation_id: user.b2b_organisation_id,
    fingerprint: user.fingerprint,
    test: isTestUser()
  }

  const posthogUserProps = posthog.get_property(STORED_PERSON_PROPERTIES_KEY) || {}
  const currentUserProps = {
    has_mobile_app: posthogUserProps.has_mobile_app,
    has_trialed: posthogUserProps.has_trialed,
    will_renew: posthogUserProps.will_renew,
    guest: posthogUserProps.guest,
    segment: posthogUserProps.segment,
    b2b_role: posthogUserProps.b2b_role,
    b2b_organisation_id: posthogUserProps.b2b_organisation_id,
    fingerprint: posthogUserProps.fingerprint,
    test: isTestUser()
  }

  const isSame = shallowEqualObjects(newUserProps, currentUserProps)

  return isSame ? null : newUserProps
}
